import { lockedApi, api } from "@/api";
import { useNotificationsStore } from "@/stores/notifications";
import { useMjmlStore } from "@/stores/mjmlStore";
import { i18nGlobal } from "../../init/i18n-setup";
import { IOpenAIMessage } from "@/schemas/openAI/openAISchema";
import axios from "axios";

const TIMEOUT_MS = 60_000; // 60 seconds

export async function getText(
  prompt: string,
  apiKey: string,
  hostname: string,
  userId: string | null | undefined,
  useDeveloperPrompt: boolean = false
): Promise<string | null> {
  const { t } = i18nGlobal;

  try {
    const res = await axios.post(
      api.GENERATE_TEXT || lockedApi.GENERATE_TEXT,
      {
        prompt: prompt,
        apiKey: apiKey,
        hostname: hostname,
        userId: userId,
        useDeveloperPrompt: useDeveloperPrompt,
      },
      { timeout: TIMEOUT_MS }
    );
    return res.data.data;
  } catch (err) {
    if (String(err).includes("Error: timeout of")) {
      useNotificationsStore().createNotification({
        type: "error",
        title: t("errors.error_occurred"),
        text: t("errors.timeout"),
      });
    } else {
      useNotificationsStore().createNotification({
        type: "error",
        title: t("errors.error_occurred"),
        text: t("errors.try_later"),
      });
    }
  }

  return null;
}

export async function getChat(
  messages: IOpenAIMessage[],
  selectedUid?: string,
  mode?: string
): Promise<IOpenAIMessage> {
  const { t } = i18nGlobal;
  const mjmlStore = useMjmlStore();

  try {
    const res = await axios.post(
      lockedApi.GENERATE_CHAT,
      {
        messages: messages,
        apiKey: mjmlStore.authorize.apiKey,
        hostname: mjmlStore.authorize.hostname,
        userId: mjmlStore.authorize.userId,
        uid: selectedUid,
        mode: mode,
      },
      { timeout: TIMEOUT_MS }
    );
    return res.data.data;
  } catch (err) {
    if (String(err).includes("Error: timeout of")) {
      console.error(err);
      throw new Error(String(err));
    } else {
      console.error(err);
      throw new Error(t("errors.try_later"));
    }
  }
}

export async function getPreheader(
  template: string,
  apiKey: string,
  hostname: string,
  userId: string | null | undefined
) {
  const { t } = i18nGlobal;

  try {
    const res = await axios.post(
      api.GENERATE_PREHEADER || lockedApi.GENERATE_PREHEADER,
      {
        template: template,
        apiKey: apiKey,
        hostname: hostname,
        userId: userId,
      },
      { timeout: TIMEOUT_MS }
    );
    return res.data.data;
  } catch (err) {
    if (String(err).includes("Error: timeout of")) {
      useNotificationsStore().createNotification({
        type: "error",
        title: t("errors.error_occurred"),
        text: t("errors.timeout"),
      });
    } else {
      useNotificationsStore().createNotification({
        type: "error",
        title: t("errors.error_occurred"),
        text: t("errors.try_later"),
      });
    }
  }

  return null;
}

type TranslationResult = {
  translations: { uid: string; content: string }[];
  detectedLanguage: string;
};

async function requestTranslation(
  keyValue: { uid: string; content: string }[],
  lang: string
): Promise<TranslationResult> {
  const mjmlStore = useMjmlStore();
  const res = await axios.post(
    api.GENERATE_TRANSLATION || lockedApi.GENERATE_TRANSLATION,
    {
      keyValue: keyValue,
      apiKey: mjmlStore.authorize.apiKey,
      hostname: mjmlStore.authorize.hostname,
      userId: mjmlStore.authorize.userId,
      lang: lang,
    },
    { timeout: TIMEOUT_MS }
  );
  return {
    translations: res.data.data,
    detectedLanguage: res.data.detectedLanguage,
  };
}

function notifyTranslationError(err: any) {
  const { t } = i18nGlobal;
  if (err?.response?.data?.errorCode === "UNRECOGNIZED_LANGUAGE") {
    useNotificationsStore().createNotification({
      type: "error",
      title: t("errors.unrecognized_language_title"),
      text: t("errors.unrecognized_language"),
    });
  } else if (String(err).includes("Error: timeout of")) {
    console.error(err);
    useNotificationsStore().createNotification({
      type: "error",
      title: t("errors.error_occurred"),
      text: t("errors.timeout"),
    });
  } else {
    console.error(err);
    useNotificationsStore().createNotification({
      type: "error",
      title: t("errors.error_occurred"),
      text: t("errors.try_later"),
    });
  }
}

export async function getTranslation(
  keyValue: { uid: string; content: string }[],
  lang: string
): Promise<TranslationResult | null> {
  try {
    return await requestTranslation(keyValue, lang);
  } catch (err: any) {
    notifyTranslationError(err);
    return null;
  }
}

function chunkPayload(
  keyValue: { uid: string; content: string }[],
  maxItems: number,
  maxChars: number
): { uid: string; content: string }[][] {
  const chunks: { uid: string; content: string }[][] = [];
  let current: { uid: string; content: string }[] = [];
  let currentChars = 0;
  for (const item of keyValue) {
    const len = item.content?.length ?? 0;
    if (
      current.length > 0 &&
      (current.length >= maxItems || currentChars + len > maxChars)
    ) {
      chunks.push(current);
      current = [];
      currentChars = 0;
    }
    current.push(item);
    currentChars += len;
  }
  if (current.length > 0) chunks.push(current);
  return chunks;
}

export async function getTranslationChunked(
  keyValue: { uid: string; content: string }[],
  lang: string,
  opts: { maxItems?: number; maxChars?: number; concurrency?: number } = {}
): Promise<TranslationResult | null> {
  if (keyValue.length === 0) {
    return { translations: [], detectedLanguage: "" };
  }

  const maxItems = opts.maxItems ?? 20;
  const maxChars = opts.maxChars ?? 8000;
  const concurrency = opts.concurrency ?? 3;

  const chunks = chunkPayload(keyValue, maxItems, maxChars);

  const translations: { uid: string; content: string }[] = [];
  let detectedLanguage = "";
  let firstError: any = null;
  let failed = 0;

  let cursor = 0;
  const worker = async () => {
    while (cursor < chunks.length) {
      const chunk = chunks[cursor++];
      try {
        const res = await requestTranslation(chunk, lang);
        translations.push(...res.translations);
        if (!detectedLanguage && res.detectedLanguage) {
          detectedLanguage = res.detectedLanguage;
        }
      } catch (err: any) {
        failed++;
        if (!firstError) firstError = err;
        // An unrecognized language fails every batch identically — stop early.
        if (err?.response?.data?.errorCode === "UNRECOGNIZED_LANGUAGE") {
          cursor = chunks.length;
        }
      }
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(concurrency, chunks.length) }, worker)
  );

  if (failed > 0) {
    notifyTranslationError(firstError);
  }
  // Nothing came back at all → treat as a failed run.
  if (translations.length === 0) {
    return null;
  }
  return { translations, detectedLanguage };
}

export async function getSection(
  prompt: string,
  template: string,
  apiKey: string,
  hostname: string,
  userId: string | null | undefined
): Promise<any | null> {
  const { t } = i18nGlobal;

  try {
    const res = await axios.post(
      lockedApi.GENERATE_SECTION,
      {
        prompt: prompt,
        template: template,
        apiKey: apiKey,
        hostname: hostname,
        userId: userId,
      },
      { timeout: 60_000 }
    );
    return res.data.data;
  } catch (err) {
    if (String(err).includes("Error: timeout of")) {
      useNotificationsStore().createNotification({
        type: "error",
        title: t("errors.error_occurred"),
        text: t("errors.timeout"),
      });
    } else {
      useNotificationsStore().createNotification({
        type: "error",
        title: t("errors.error_occurred"),
        text: t("errors.try_later"),
      });
    }
  }

  return null;
}

// export async function getKeywords(prompt: string): Promise<string[]> {
//   const res = await axios.post(lockedApi.TLDR, {
//     prompt: prompt,
//   });

//   const trimmed = res.data.keywords.trim();
//   return trimmed.split(",");
// }
